// Carrega e monta as agendas de recebível de TODA a carteira.
//
// Saiu de `src/hooks/useAgendasRecebiveis.ts` para cá quando o catálogo da Vick
// passou a responder "quanto vou receber em outubro". A tela e a IA têm que
// devolver o MESMO número — se a IA tivesse a sua própria busca, a diretoria
// perguntaria para a Vick e conferiria na tela, achando divergência.
//
// O client entra por parâmetro (`ClienteSupabase`) porque os dois lados usam
// clients diferentes: a tela usa o do navegador, com a sessão do usuário; a
// edge function usa o service_role. A regra é a mesma, o acesso não.
//
// ⚠️ A paginação NÃO é detalhe: o PostgREST corta em 1.000 linhas sem erro e
// sem aviso. Já mordeu Analytics, o Kanban do CRM e a projeção de faturamento.
//
// Função de dados, não pura — o que dá para testar sem I/O está em
// `recebiveisUc.ts` e `recebivelUc.ts`, que é onde mora a conta.

import {
  deduplicarUcs,
  montarAgendaDaUc,
  calcularTaxaRecebimento,
  entradaDaTaxa,
  COLUNAS_FATURA_RECEBIVEL,
  type AgendaDaUc,
  type FaturaRecebivel,
  type TaxaRecebimento,
  type UcRecebivel,
} from './recebiveisUc.ts';

/** O mínimo que este módulo usa de um client Supabase — serve para o do
 *  navegador e para o de service_role, sem depender do tipo do SDK. */
export interface ClienteSupabase {
  from(tabela: string): any;
}

const PAGINA = 1000;

/**
 * Janela de histórico buscada. A projeção usa 12 meses de consumo; 15 dá folga
 * para meses faltando sem carregar a base inteira, que cresce para sempre.
 */
export const MESES_HISTORICO = 15;

export const COLUNAS_UC_RECEBIVEL =
  'id, numero_uc, numero_uc_norm, outro_numero_uc_norm, nome_cliente, situacao, ' +
  'desconto_tarifa, desconto_bandeira, empresa_parceira_id, tipo_ligacao, ' +
  'cidade, estado, concessionaria, grupo_tarifario, negociacao_id';

export function inicioDaJanela(hoje = new Date()): string {
  const d = new Date(Date.UTC(hoje.getUTCFullYear(), hoje.getUTCMonth() - MESES_HISTORICO, 1));
  return d.toISOString().slice(0, 10);
}

/** Busca paginada — o PostgREST corta em 1.000 linhas sem erro e sem aviso. */
export async function buscarTudo<T>(
  cliente: ClienteSupabase,
  tabela: string,
  colunas: string,
  ordenar: string,
  filtrar?: (q: any) => any,
): Promise<T[]> {
  const out: T[] = [];
  for (let offset = 0; ; offset += PAGINA) {
    let q = cliente.from(tabela).select(colunas);
    if (filtrar) q = filtrar(q);
    const { data, error } = await q.order(ordenar).range(offset, offset + PAGINA - 1);
    if (error) throw error;
    if (!data?.length) break;
    out.push(...(data as T[]));
    if (data.length < PAGINA) break;
  }
  return out;
}

export interface DadosRecebiveis {
  agendas: AgendaDaUc[];
  /** Cadastros duplicados absorvidos — o mesmo cliente cadastrado duas vezes. */
  ucsDuplicadas: number;
  /** Fração do emitido que historicamente entra, medida no próprio histórico. */
  taxaRecebimento: TaxaRecebimento;
  empresasParceiras: { id: string; nome: string }[];
}

/**
 * A busca em si. Chamada pelo hook da tela, pelo export do Excel e pelo catálogo
 * da Vick — os três leem o mesmo número porque leem a mesma função.
 */
export async function buscarAgendasRecebiveis(
  cliente: ClienteSupabase,
  { horizonteMeses = 6 }: { horizonteMeses?: number } = {},
): Promise<DadosRecebiveis> {
  const hoje = new Date();
  const hojeIso = hoje.toISOString().slice(0, 10);

  const [ucs, faturas, parceiras] = await Promise.all([
    buscarTudo<UcRecebivel>(cliente, 'unidades_consumidoras', COLUNAS_UC_RECEBIVEL, 'id'),
    buscarTudo<FaturaRecebivel & { uc_codigo_norm?: string | null }>(
      cliente,
      'faturas',
      COLUNAS_FATURA_RECEBIVEL,
      'id',
      // Sem limite superior: 514 faturas já existem com vencimento FUTURO
      // (parcelamentos e ciclos adiantados), e são justamente elas que
      // impedem a projeção de contar duas vezes o mesmo mês.
      (q: any) => q.gte('data_vencimento', inicioDaJanela(hoje)),
    ),
    cliente.from('empresas_parceiras').select('id, nome, percentual_vigor').order('nome'),
  ]);
  if (parceiras.error) throw parceiras.error;

  const pctVigor = new Map<string, number>(
    (parceiras.data || []).map((p: any) => [p.id as string, Number(p.percentual_vigor) || 0]),
  );

  const porCodigo = new Map<string, FaturaRecebivel[]>();
  for (const f of faturas) {
    const cod = f.uc_codigo_norm;
    if (!cod) continue;
    if (!porCodigo.has(cod)) porCodigo.set(cod, []);
    porCodigo.get(cod)!.push(f);
  }

  // O MESMO cliente aparece cadastrado duas vezes, com os dois números de UC
  // trocados entre os registros — 4,7% da carteira contados em dobro.
  //
  // Canceladas ENTRAM: ainda cobram o saldo pendente (R$ 105 mil em AGO/2026) —
  // a projeção delas decai mês a mês em `montarAgendaDaUc`. Vão para o FIM da
  // lista antes da dedupe: quando o mesmo número de UC tem um cadastro vivo e
  // um cancelado (recadastro), o vivo é quem fica — senão a UC ativa perderia a
  // projeção de consumo e passaria a decair como cancelada.
  const ordenadas = [
    ...ucs.filter((u) => u.situacao !== 'Cancelada'),
    ...ucs.filter((u) => u.situacao === 'Cancelada'),
  ];
  const { ucs: unicas, duplicadas } = deduplicarUcs(ordenadas);

  const agendas: AgendaDaUc[] = [];
  for (const uc of unicas) {
    // A UC casa ora por `numero_uc_norm`, ora por `outro_numero_uc_norm`.
    const lista = [
      ...(porCodigo.get(uc.numero_uc_norm || '') || []),
      ...(porCodigo.get(uc.outro_numero_uc_norm || '') || []),
    ];
    if (lista.length === 0) continue;
    const a = montarAgendaDaUc({
      uc,
      faturas: lista,
      percentualVigor: uc.empresa_parceira_id ? pctVigor.get(uc.empresa_parceira_id) : null,
      horizonteMeses,
      hoje,
    });
    if (a.meses.length > 0) agendas.push(a);
  }

  // A taxa sai das MESMAS faturas que alimentam a projeção — assim o
  // "a receber" e o "a faturar" nunca falam de universos diferentes.
  const taxaRecebimento = calcularTaxaRecebimento(
    faturas.filter((f) => f.ambiente !== 'sandbox').map((f) => entradaDaTaxa(f, hoje)),
    hojeIso,
  );

  return {
    agendas,
    ucsDuplicadas: duplicadas.length,
    taxaRecebimento,
    empresasParceiras: (parceiras.data || []).map((p: any) => ({ id: p.id, nome: p.nome })),
  };
}
